import { useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { Menu, X, Globe, Smartphone, User, LogOut } from "lucide-react";
const logo = "/logo.webp";
import { Button } from "@/components/ui/button";
import { useTranslation } from "@/i18n/LanguageContext";
import { useAuthSession } from "@/hooks/use-auth-session";
import { supabase } from "@/integrations/supabase/client";
import { clearSmsMfaVerified } from "@/hooks/use-mfa";
import { useSiteSetting } from "@/hooks/use-site-setting";

export function Navbar() {
  const [open, setOpen] = useState(false);
  const { t, locale, setLocale } = useTranslation();
  const location = useLocation();
  const navigate = useNavigate();
  const { user } = useAuthSession();

  const navLinks = [
    { label: t("nav.home"), href: "#hero"
  },
    { label: t("nav.about"), href: "#about"
  },
    { label: t("nav.services"), href: "#services"
  },
    { label: t("nav.industries"), href: "#industries"
  },
    { label: t("nav.whyUs"), href: "#why-us"
  },
    { label: t("nav.contact"), href: "#contact"
  },
  ];

  const { value: pricingVisible } = useSiteSetting<boolean>("nav.pricing_visible", true);
  const { value: trainingsVisible } = useSiteSetting<boolean>("nav.trainings_visible", true);
  const { value: offersVisible } = useSiteSetting<boolean>("nav.offers_visible", true);

  const routeLinks = [
    ...(offersVisible ? [{ label: locale === "fr" ? "Nos Offres" : "Our Offers", to: "/campagnes" }] : []),
    ...(pricingVisible ? [{ label: locale === "fr" ? "Tarifs" : "Pricing", to: "/pricing"
  }] : []),
    ...(trainingsVisible ? [{ label: locale === "fr" ? "Formations" : "Trainings", to: "/formations"
  }] : []),
  ];



  const scrollTo = (id: string) => {
    setOpen(false);
    if (location.pathname !== "/") {
      navigate("/" + id);
    } else {
      const el = document.querySelector(id);
      el?.scrollIntoView({ behavior: "smooth"
  });
    }
  };

  const toggleLang = () => setLocale(locale === "fr" ? "en" : "fr");

  const handleLogout = async () => {
    clearSmsMfaVerified();
    await supabase.auth.signOut();
    window.location.href = "/";
  };

  const fullName = user?.user_metadata?.full_name || user?.email?.split("@")[0] || "";
  const firstName = fullName.split(" ")[0];

  return (
    <nav className="fixed top-0 left-0 right-0 z-50 navbar-dark">
      <div className="container flex items-center justify-between h-16">
        <Link to="/"
  className="flex items-center gap-2 shrink-0" style={{ width: 220, height: 48 }}>
          <img
            src={logo}
            alt="CloudMature - Conseil Cloud, DevOps et Intelligence Artificielle"
            width={220}
            height={48}
            fetchPriority="high"
            decoding="async"
            loading="eager"
  className="block brightness-[1.15] contrast-[1.1]"
            style={{ width: 220, height: 48 }}
          />
        </Link>

        {/* Desktop */}
        <div className="hidden lg:flex items-center gap-6">
          {navLinks.map((l) => (
            <button key={l.href} onClick={() => scrollTo(l.href)} className="text-sm font-medium text-secondary-foreground/80 hover:text-primary transition-colors">
              {l.label}
            </button>
          ))}
          {routeLinks.map((l) => (
            <Link key={l.to} to={l.to} className="text-sm font-medium text-secondary-foreground/80 hover:text-primary transition-colors">
              {l.label}
            </Link>
          ))}
          <button
            onClick={toggleLang}
            className="flex items-center gap-1.5 text-sm font-medium text-secondary-foreground/80 hover:text-primary transition-colors"
          >
            <Globe size={16} />
            {locale === "fr" ? "EN" : "FR"}
          </button>

          {user ? (
            <div className="flex items-center gap-2">
              <Link to="/portal"
  className="flex items-center gap-2.5 px-4 py-2 rounded-full bg-primary/15 border border-primary/30 hover:bg-primary/25 transition-colors">
                <div className="w-7 h-7 rounded-full gradient-primary flex items-center justify-center shadow-sm">
                  <User size={14} className="text-primary-foreground" />
                </div>
                <span className="text-sm font-semibold text-primary">{firstName}</span>
              </Link>
              <button
                onClick={handleLogout}
                className="p-1.5 rounded-lg text-secondary-foreground/70 hover:text-destructive hover:bg-destructive/10 transition-colors"
                title={locale === "fr" ? "Déconnexion" : "Sign out"}
                aria-label={locale === "fr" ? "Déconnexion" : "Sign out"}
              >
                <LogOut size={16} />
              </button>
            </div>
          ) : (
            <Link to="/portal">
              <Button size="sm"
  className="gradient-primary text-primary-foreground border-0 hover:opacity-90">
                {t("nav.portal")}
              </Button>
            </Link>
          )}
        </div>

        {/* Mobile hamburger */}
        <button
          className="lg:hidden p-2 rounded-lg text-white bg-white/10 hover:bg-white/20 border border-white/20 transition-colors"
          onClick={() => setOpen(!open)}
          aria-label={open ? "Fermer le menu" : "Ouvrir le menu"}
          aria-expanded={open}
        >
          {open ? <X size={22} /> : <Menu size={22} />}
        </button>
      </div>

      {open && (
        <div className="lg:hidden gradient-hero border-t border-border/20 pb-4">
          {navLinks.map((l) => (
            <button key={l.href} onClick={() => scrollTo(l.href)} className="block w-full text-left px-6 py-3 text-sm text-secondary-foreground hover:bg-secondary/50">
              {l.label}
            </button>
          ))}
          {routeLinks.map((l) => (
            <Link key={l.to} to={l.to} onClick={() => setOpen(false)} className="block w-full text-left px-6 py-3 text-sm text-secondary-foreground hover:bg-secondary/50">
              {l.label}
            </Link>
          ))}
          <button
            onClick={toggleLang}
            className="block w-full text-left px-6 py-3 text-sm text-secondary-foreground hover:bg-secondary/50 flex items-center gap-2"
          >
            <Globe size={16} />
            {locale === "fr" ? "English" : "Français"}
          </button>
          <Link
            to="/install"
            onClick={() => setOpen(false)}
            className="block w-full text-left px-6 py-3 text-sm text-secondary-foreground hover:bg-secondary/50 flex items-center gap-2"
          >
            <Smartphone size={16} />
            {t("install.navButton")}
          </Link>
          <div className="px-6 pt-2">
            {user ? (
              <div className="space-y-2">
                <Link to="/portal" onClick={() => setOpen(false)} className="flex items-center gap-3 px-3 py-2 rounded-xl bg-primary/10 border border-primary/20">
                  <div className="w-8 h-8 rounded-full gradient-primary flex items-center justify-center">
                    <User size={16} className="text-primary-foreground" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-semibold text-foreground truncate">{firstName}</p>
                    <p className="text-xs text-muted-foreground">{t("nav.portal")}</p>
                  </div>
                </Link>
                <button
                  onClick={() => { setOpen(false); handleLogout(); }}
                  className="w-full flex items-center gap-2 px-3 py-2 text-sm text-destructive hover:bg-destructive/10 rounded-lg transition-colors"
                >
                  <LogOut size={16} />
                  {locale === "fr" ? "Déconnexion" : "Sign out"}
                </button>
              </div>
            ) : (
              <Link to="/portal" onClick={() => setOpen(false)}>
                <Button size="sm"
  className="w-full gradient-primary text-primary-foreground border-0">{t("nav.portal")}</Button>
              </Link>
            )}
          </div>
        </div>
      )}
    </nav>
  );
}
